import { useState, useEffect, useMemo, useCallback } from 'react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { PageHeader } from '@/components/layout/PageHeader'
import { Printer } from 'lucide-react'
import { ExcelGrid } from '@/components/excelGrid/ExcelGrid'
import { type ExcelColumnDef, getColHeader, getColVal, getColKey } from '@/components/excelGrid/types'
import { exportToCsv } from '@/components/excelGrid/tsvUtils'
import {
  listStockIssues,
  recordStockIssue,
  patchStockIssue,
  voidStockIssue,
  getStockIssueHistory,
  getStockOverview,
  recordStockIssuePrintLog,
} from '@/services/inventory/apiClient'
import type { StockIssueDto, MaterialStockOverviewItem } from '@/services/inventory/types'
import { getGroupDef, formatMaterialAttributes } from '@/domain/materialGroups'
import { useCan, useAuth } from '@/features/auth/AuthContext'
import type { DocumentChangeLogDto } from '@/services/procurement/types'
import { formatDateTime, formatDateTimeFull } from '@/lib/dateFormat'
import {
  MaterialTypeahead,
  matchMaterialFromExcelText,
} from '@/components/forms/MaterialTypeahead'
import { Typeahead } from '@/components/forms/Typeahead'
import {
  WarehouseDocumentPrintModal,
  type PrintDocumentData,
  type PrintDocumentLine,
} from './WarehouseDocumentPrintModal'

const DEPT_SUGGESTIONS = [
  'Xưởng mộc',
  'Xưởng sơn',
  'Kỹ thuật',
  'Mua hàng',
  'Kế hoạch',
  'Kho vận',
  'Văn phòng',
  'Bảo trì',
]

function fmtN(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '0'
  const num = typeof n === 'number' ? n : Number(n)
  if (!Number.isFinite(num)) return '0'
  return num.toLocaleString('vi-VN', { maximumFractionDigits: 2 })
}

type IssueFlattenedRow = {
  issueId: string
  code: string
  purpose: string
  materialId: string
  materialName: string
  groupCode?: string | null
  uom: string
  qty: number
  moNumber?: string | null
  orderAndCustomer: string
  at: string
  by: string
  receiverName?: string
  receiverDepartment?: string
  status: string
  isVoid: boolean
  hasMo: boolean
  editCount: number
  voidReason: string
  note: string
  rawIssue: StockIssueDto
}

export function IssuesPage() {
  const { user } = useAuth()
  const canWriteInventory = useCan('inventory.write') || (user?.roles ? user.roles.some((r) => r === 'WAREHOUSE') : false)

  const [searchQuery, setSearchQuery] = useState('')
  const [resetWidthsCounter, setResetWidthsCounter] = useState(0)
  const [loading, setLoading] = useState(true)
  const [toastMessage, setToastMessage] = useState<string | null>(null)

  const [issues, setIssues] = useState<StockIssueDto[]>([])
  const [materials, setMaterials] = useState<MaterialStockOverviewItem[]>([])

  // Print modal state
  const [printModalOpen, setPrintModalOpen] = useState(false)
  const [printDocData, setPrintDocData] = useState<PrintDocumentData | null>(null)
  const [printIssueId, setPrintIssueId] = useState<string | null>(null)

  // Modal detail / edit
  const [selectedIssue, setSelectedIssue] = useState<StockIssueDto | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editLineQtys, setEditLineQtys] = useState<Record<string, number>>({})
  const [editLineNotes, setEditLineNotes] = useState<Record<string, string>>({})
  const [editReceiverName, setEditReceiverName] = useState('')
  const [editReceiverDepartment, setEditReceiverDepartment] = useState('')
  const [editReason, setEditReason] = useState('')
  const [issueHistory, setIssueHistory] = useState<DocumentChangeLogDto[]>([])

  // Modal new issue
  const [newModalOpen, setNewModalOpen] = useState(false)
  const [newPurpose, setNewPurpose] = useState('Xuất sửa chữa / mẫu')
  const [newReceiverName, setNewReceiverName] = useState('')
  const [newReceiverDepartment, setNewReceiverDepartment] = useState('')
  const [newNote, setNewNote] = useState('')
  const [newLines, setNewLines] = useState<Array<{ materialId: string; qty: number | string; note: string; error?: string }>>([
    { materialId: '', qty: '', note: '' },
    { materialId: '', qty: '', note: '' },
    { materialId: '', qty: '', note: '' },
  ])

  const receiverSuggestions = useMemo(() => {
    const set = new Set<string>()
    issues.forEach((i) => {
      if (i.receiver_name) set.add(i.receiver_name.trim())
    })
    return Array.from(set)
  }, [issues])

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    setTimeout(() => setToastMessage(null), 3500)
  }, [])

  const handlePasteNewLines = useCallback((e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain')
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return
    e.preventDefault()

    const rows = text.split(/\r?\n/).filter((r) => r.trim())
    const parsedRows: Array<{ materialId: string; qty: number | string; note: string; error?: string }> = []

    for (const r of rows) {
      const cols = r.split('\t')
      const rawMatName = cols[0]?.trim() || ''
      const rawQty = cols[1]?.trim() || ''
      const rawNote = cols[2]?.trim() || ''

      if (!rawMatName) continue

      const matchRes = matchMaterialFromExcelText(rawMatName, materials)
      let matId = ''
      let err: string | undefined = undefined

      if (matchRes.material) {
        matId = matchRes.material.material_id
      } else if (matchRes.ambiguous) {
        err = 'Chưa chọn thông số — bấm để chọn'
      } else {
        err = `Không khớp: "${rawMatName}"`
      }

      parsedRows.push({
        materialId: matId,
        qty: rawQty && !isNaN(Number(rawQty)) ? Number(rawQty) : '',
        note: rawNote,
        error: err,
      })
    }

    if (parsedRows.length > 0) {
      setNewLines((prev) => {
        const existing = prev.filter((l) => l.materialId || l.qty)
        return [...existing, ...parsedRows]
      })
      showToast(`Đã dán ${parsedRows.length} dòng từ Excel`)
    }
  }, [materials, showToast])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [issueList, matList] = await Promise.all([
        listStockIssues().catch(() => []),
        getStockOverview().catch(() => []),
      ])
      setIssues(issueList)
      setMaterials(matList)
    } catch {
      showToast('Lỗi khi tải dữ liệu phiếu xuất kho')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const materialMap = useMemo(() => {
    const map = new Map<string, MaterialStockOverviewItem>()
    for (const m of materials) {
      map.set(m.material_id, m)
    }
    return map
  }, [materials])

  // Flatten issues into row-per-line matching demo
  const flattenedRows: IssueFlattenedRow[] = useMemo(() => {
    const out: IssueFlattenedRow[] = []
    for (const d of issues) {
      const lines = d.lines || []
      const orderAndCustomer = [d.sales_order_code, d.customer_name].filter(Boolean).join(' · ')
      for (const l of lines) {
        const mat = materialMap.get(l.material_id)
        out.push({
          issueId: d.id,
          code: d.code,
          purpose: d.purpose,
          materialId: l.material_id,
          materialName: l.material_name || mat?.material_name || 'Vật tư',
          groupCode: l.group_code || mat?.group_code,
          uom: l.base_uom || mat?.base_uom || '',
          qty: Number(l.qty_base || 0),
          moNumber: d.mo_number,
          orderAndCustomer: orderAndCustomer || '—',
          at: d.created_at,
          by: d.created_by,
          receiverName: d.receiver_name || '',
          receiverDepartment: d.receiver_department || '',
          status: d.status === 'VOID' ? 'Đã hủy' : d.mo_number ? 'Theo LSX' : 'Hoạt động',
          isVoid: d.status === 'VOID',
          hasMo: Boolean(d.mo_number),
          editCount: d.edit_count || 0,
          voidReason: d.void_reason || '',
          note: l.note || d.note || '',
          rawIssue: d,
        })
      }
    }
    return out
  }, [issues, materialMap])

  // Print issue helper
  const handleOpenPrintIssue = useCallback((issue: StockIssueDto) => {
    const lines: PrintDocumentLine[] = (issue.lines || []).map((l) => {
      const mat = materialMap.get(l.material_id)
      const attrs = mat ? formatMaterialAttributes(mat.group_code, mat.attributes) : ''
      return {
        material_name: l.material_name || mat?.material_name || mat?.name || 'Vật tư',
        attributes_desc: attrs,
        uom: l.base_uom || mat?.base_uom || '',
        qty: Number(l.qty_base || 0),
        note: l.note || undefined,
      }
    })

    const orderParts = [
      issue.mo_number ? `LSX ${issue.mo_number}` : '',
      issue.sales_order_code ? `ĐH ${issue.sales_order_code}` : '',
      issue.customer_name ? `Khách: ${issue.customer_name}` : '',
      issue.customer_po ? `PO: ${issue.customer_po}` : '',
    ].filter(Boolean).join(' · ')

    setPrintDocData({
      type: 'ISSUE',
      code: issue.code,
      date: issue.created_at,
      created_by: issue.created_by,
      status: issue.status,
      void_reason: issue.void_reason,
      edit_count: issue.edit_count,
      receiver_name: issue.receiver_name,
      receiver_department: issue.receiver_department,
      purpose: issue.purpose,
      order_info: orderParts || undefined,
      note: issue.note,
      lines,
    })
    setPrintIssueId(issue.id)
    setPrintModalOpen(true)
  }, [materialMap])

  // Grid columns matching demo
  const cols: ExcelColumnDef<IssueFlattenedRow>[] = useMemo(() => [
    {
      key: 'code',
      header: 'Phiếu',
      width: 130,
      isLink: true,
      tooltip: () => 'Bấm để xem / sửa phiếu',
      getValue: (r) => r.code,
    },
    {
      key: 'purpose',
      header: 'Mục đích',
      width: 190,
      getValue: (r) => r.purpose,
    },
    {
      key: 'materialName',
      header: 'Vật tư',
      width: 200,
      getValue: (r) => r.materialName,
    },
    {
      key: 'groupCode',
      header: 'Nhóm',
      width: 130,
      getValue: (r) => getGroupDef(r.groupCode).name,
    },
    {
      key: 'uom',
      header: 'ĐVT',
      width: 60,
      align: 'center',
      getValue: (r) => r.uom,
    },
    {
      key: 'qty',
      header: 'SL xuất',
      width: 90,
      isNumeric: true,
      getValue: (r) => r.qty,
      renderCell: (r) => fmtN(r.qty),
    },
    {
      key: 'moNumber',
      header: 'Lệnh SX',
      width: 100,
      align: 'center',
      getValue: (r) => r.moNumber || '',
      renderCell: (r) => (r.moNumber ? <b>{r.moNumber}</b> : <span style={{ color: '#888' }}>—</span>),
    },
    {
      key: 'orderAndCustomer',
      header: 'Đơn hàng · Khách',
      width: 230,
      getValue: (r) => r.orderAndCustomer,
    },
    {
      key: 'receiverName',
      header: 'Người nhận',
      width: 130,
      getValue: (r) => r.receiverName || '—',
    },
    {
      key: 'receiverDepartment',
      header: 'Bộ phận',
      width: 110,
      getValue: (r) => r.receiverDepartment || '—',
    },
    {
      key: 'at',
      header: 'Ngày giờ',
      width: 110,
      align: 'center',
      getValue: (r) => formatDateTime(r.at),
      renderCell: (r) => (
        <span title={formatDateTimeFull(r.at)}>{formatDateTime(r.at)}</span>
      ),
    },
    {
      key: 'by',
      header: 'Người xuất',
      width: 130,
      getValue: (r) => r.by,
    },
    {
      key: 'status',
      header: 'Trạng thái',
      width: 100,
      getValue: (r) => r.status,
      renderCell: (r) => {
        if (r.isVoid) return <span style={{ color: '#999' }}>Đã hủy</span>
        if (r.hasMo) return <span style={{ color: '#0b4fb3', fontWeight: 600 }}>Theo LSX</span>
        return <span style={{ color: '#1b6e2d', fontWeight: 600 }}>Hoạt động</span>
      },
    },
    {
      key: 'editCount',
      header: 'Số lần sửa',
      width: 80,
      isNumeric: true,
      getValue: (r) => r.editCount,
    },
    {
      key: 'voidReason',
      header: 'Lý do hủy',
      width: 180,
      getValue: (r) => r.voidReason,
    },
    {
      key: 'note',
      header: 'Ghi chú',
      width: 220,
      getValue: (r) => r.note,
      tooltip: (r) => r.note || undefined,
    },
    {
      key: 'actions',
      header: 'In',
      width: 60,
      align: 'center',
      getValue: () => '',
      renderCell: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleOpenPrintIssue(r.rawIssue)
          }}
          title="In phiếu xuất kho"
          style={{
            background: 'none',
            border: '1px solid #ccc',
            borderRadius: 3,
            padding: '2px 6px',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          <Printer size={14} aria-hidden="true" />
        </button>
      ),
    },
  ], [handleOpenPrintIssue])

  // Open detail modal
  const handleOpenDocModal = useCallback(async (issue: StockIssueDto) => {
    setSelectedIssue(issue)
    setIsEditing(false)
    setEditReason('')
    setEditReceiverName(issue.receiver_name || '')
    setEditReceiverDepartment(issue.receiver_department || '')
    const initQtys: Record<string, number> = {}
    const initNotes: Record<string, string> = {}
    ;(issue.lines || []).forEach((l) => {
      initQtys[l.id] = Number(l.qty_base || 0)
      initNotes[l.id] = l.note || ''
    })
    setEditLineQtys(initQtys)
    setEditLineNotes(initNotes)

    try {
      const hist = await getStockIssueHistory(issue.id)
      setIssueHistory(hist)
    } catch {
      setIssueHistory([])
    }
  }, [])

  // Save issue edit
  const handleSaveIssueEdit = async () => {
    if (!selectedIssue) return
    const reason = editReason.trim()
    if (!reason) {
      showToast('Bắt buộc nhập lý do chỉnh sửa')
      return
    }

    const hadReceiverName = Boolean(selectedIssue.receiver_name && selectedIssue.receiver_name.trim())
    const hadReceiverDept = Boolean(selectedIssue.receiver_department && selectedIssue.receiver_department.trim())

    if (hadReceiverName && !editReceiverName.trim()) {
      showToast('Người nhận hàng không được để trống khi sửa phiếu đã có người nhận')
      return
    }
    if (hadReceiverDept && !editReceiverDepartment.trim()) {
      showToast('Bộ phận không được để trống khi sửa phiếu đã có bộ phận')
      return
    }

    const lines = (selectedIssue.lines || []).map((l) => ({
      id: l.id,
      qty_base: editLineQtys[l.id] ?? Number(l.qty_base || 0),
      note: editLineNotes[l.id] ?? l.note,
    }))

    try {
      await patchStockIssue(selectedIssue.id, {
        receiver_name: editReceiverName.trim() || null,
        receiver_department: editReceiverDepartment.trim() || null,
        lines,
        reason,
      })
      showToast(`Đã cập nhật phiếu ${selectedIssue.code}`)
      setSelectedIssue(null)
      await loadData()
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi cập nhật phiếu xuất')
    }
  }

  // Void issue
  const handleVoidIssue = async () => {
    if (!selectedIssue) return
    const reason = window.prompt(`Lý do hủy phiếu ${selectedIssue.code} (bắt buộc):`)
    if (reason === null) return
    if (!reason.trim()) {
      showToast('Bắt buộc nhập lý do hủy')
      return
    }

    try {
      await voidStockIssue(selectedIssue.id, reason.trim())
      showToast(`Đã hủy phiếu ${selectedIssue.code}`)
      setSelectedIssue(null)
      await loadData()
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi hủy phiếu xuất')
    }
  }

  // Save new manual issue
  const handleSaveNewIssue = async () => {
    if (!newReceiverName.trim()) {
      showToast('Bắt buộc nhập họ tên người nhận hàng')
      return
    }
    if (!newReceiverDepartment.trim()) {
      showToast('Bắt buộc nhập bộ phận người nhận')
      return
    }

    const validLines = newLines
      .filter((l) => l.materialId && Number(l.qty) > 0)
      .map((l) => ({
        material_id: l.materialId,
        qty_base: Number(l.qty),
        note: l.note.trim() || null,
      }))

    if (validLines.length === 0) {
      showToast('Chưa có dòng hợp lệ (cần chọn vật tư và số lượng > 0)')
      return
    }

    try {
      const created = await recordStockIssue({
        purpose: newPurpose,
        receiver_name: newReceiverName.trim(),
        receiver_department: newReceiverDepartment.trim(),
        note: newNote.trim() || null,
        lines: validLines,
      })
      showToast(`Đã tạo phiếu xuất kho ${created.code}`)
      setNewModalOpen(false)
      await loadData()
      handleOpenPrintIssue(created as any)
    } catch (err: any) {
      showToast(err.message || 'Lỗi khi tạo phiếu xuất')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%', padding: '8px 12px', background: '#fff', boxSizing: 'border-box' }}>
      <Breadcrumb items={[{ label: 'Kho', to: '/warehouse/stock' }, { label: 'Xuất kho' }]} />
      <PageHeader title="Xuất kho" subtitle="Tạo và theo dõi phiếu xuất kho" />
      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: 'fixed',
            right: 14,
            bottom: 14,
            background: '#222',
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 3,
            zIndex: 9999,
            fontSize: 13,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {toastMessage}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Tìm trong bảng…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: 220, padding: '4px 8px', border: '1px solid #aaa', borderRadius: 2 }}
        />

        <span style={{ color: '#777', fontSize: 13, marginLeft: 4 }}>
          Phiếu xuất theo lệnh SX tạo ở màn Thực hiện sản xuất; ở đây xem, sửa, trả lại kho
        </span>

        <div style={{ flex: 1 }} />

        <button
          onClick={() => {
            setNewLines([
              { materialId: '', qty: '', note: '' },
              { materialId: '', qty: '', note: '' },
              { materialId: '', qty: '', note: '' },
            ])
            setNewReceiverName('')
            setNewReceiverDepartment('')
            setNewNote('')
            setNewPurpose('Xuất sửa chữa / mẫu')
            setNewModalOpen(true)
          }}
          disabled={!canWriteInventory}
          style={{
            padding: '5px 12px',
            background: canWriteInventory ? '#1f6feb' : '#999',
            color: '#fff',
            border: 'none',
            borderRadius: 3,
            cursor: canWriteInventory ? 'pointer' : 'not-allowed',
            fontWeight: 600,
          }}
        >
          ＋ Phiếu xuất
        </button>

        <button
          onClick={() => setSearchQuery('')}
          disabled={!searchQuery}
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: searchQuery ? 'pointer' : 'default',
            color: searchQuery ? '#333' : '#999',
          }}
        >
          ✕ Bỏ lọc
        </button>

        <button
          onClick={() => setResetWidthsCounter((c) => c + 1)}
          title="Đặt lại độ rộng các cột về mặc định"
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          ↔ Đặt lại độ rộng
        </button>

        <button
          onClick={() => {
            const headers = cols.map((c) => getColHeader(c))
            const rows = flattenedRows.map((r) => cols.map((c) => getColVal(c, r)))
            exportToCsv('xuat-hang.csv', headers, rows)
            showToast('Đã xuất file CSV (mở được bằng Excel)')
          }}
          style={{
            padding: '5px 10px',
            background: '#f6f8fa',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: 'pointer',
          }}
        >
          ⬇ Excel
        </button>
      </div>

      {/* Grid Container */}
      <div style={{ flex: 1, minHeight: 0, border: '1px solid #ccc' }}>
        <ExcelGrid<IssueFlattenedRow>
          id="iss"
          columns={cols}
          data={flattenedRows}
          searchQuery={searchQuery}
          freeze={2}
          resetWidthsTrigger={resetWidthsCounter}
          rowCls={(r: IssueFlattenedRow) => (r.isVoid ? 'void' : undefined)}
          emptyMessage={loading ? 'Đang tải dữ liệu phiếu xuất…' : 'Chưa có phiếu xuất nào'}
          onCellClick={(r, col) => {
            if (getColKey(col) === 'code') {
              void handleOpenDocModal(r.rawIssue)
            }
          }}
        />
      </div>

      {/* MODAL: Xem / Sửa phiếu xuất */}
      {selectedIssue && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #777',
              width: 'min(980px, 98vw)',
              maxHeight: '94vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
            }}
          >
            {/* Header */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderBottom: '1px solid #bbb', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Phiếu xuất {selectedIssue.code}</span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => setSelectedIssue(null)}
                style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '10px 14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Chips */}
              <div style={{ display: 'flex', gap: 16, padding: '6px 10px', background: '#fafafa', border: '1px solid #ddd', fontSize: 13, flexWrap: 'wrap' }}>
                <span>Phiếu <b>{selectedIssue.code}</b></span>
                <span>Mục đích <b>{selectedIssue.purpose}</b></span>
                <span>Người nhận <b>{selectedIssue.receiver_name || '—'}</b></span>
                <span>Bộ phận <b>{selectedIssue.receiver_department || '—'}</b></span>
                {selectedIssue.mo_number && <span>Lệnh SX <b>{selectedIssue.mo_number}</b></span>}
                <span>{formatDateTime(selectedIssue.created_at)} · {selectedIssue.created_by}</span>
                <span style={{ color: selectedIssue.status === 'VOID' ? '#999' : '#1b6e2d', fontWeight: 600 }}>
                  {selectedIssue.status === 'VOID' ? 'Đã hủy' : selectedIssue.mo_number ? 'Theo LSX' : 'Hoạt động'}
                </span>
              </div>

              {/* Editing receiver info */}
              {isEditing && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: '8px 10px', background: '#f0f7ff', border: '1px solid #cce0ff' }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#333', display: 'block', marginBottom: 2, fontWeight: 600 }}>
                      Người nhận hàng {selectedIssue.receiver_name ? <span style={{ color: '#d00' }}>*</span> : null}
                    </label>
                    <input
                      type="text"
                      value={editReceiverName}
                      onChange={(e) => setEditReceiverName(e.target.value)}
                      placeholder="Họ tên người nhận..."
                      style={{
                        width: '100%',
                        border: Boolean(selectedIssue.receiver_name && !editReceiverName.trim()) ? '1px solid #d00' : '1px solid #8aa',
                        padding: '4px 6px',
                        boxSizing: 'border-box',
                      }}
                    />
                    {Boolean(selectedIssue.receiver_name && !editReceiverName.trim()) && (
                      <div style={{ color: '#d00', fontSize: 11, marginTop: 2 }}>Bắt buộc nhập người nhận hàng</div>
                    )}
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#333', display: 'block', marginBottom: 2, fontWeight: 600 }}>
                      Bộ phận {selectedIssue.receiver_department ? <span style={{ color: '#d00' }}>*</span> : null}
                    </label>
                    <input
                      type="text"
                      list="edit-dept-list"
                      value={editReceiverDepartment}
                      onChange={(e) => setEditReceiverDepartment(e.target.value)}
                      placeholder="Xưởng mộc, Xưởng sơn..."
                      style={{
                        width: '100%',
                        border: Boolean(selectedIssue.receiver_department && !editReceiverDepartment.trim()) ? '1px solid #d00' : '1px solid #8aa',
                        padding: '4px 6px',
                        boxSizing: 'border-box',
                      }}
                    />
                    <datalist id="edit-dept-list">
                      {DEPT_SUGGESTIONS.map((d) => (
                        <option key={d} value={d} />
                      ))}
                    </datalist>
                    {Boolean(selectedIssue.receiver_department && !editReceiverDepartment.trim()) && (
                      <div style={{ color: '#d00', fontSize: 11, marginTop: 2 }}>Bắt buộc nhập bộ phận</div>
                    )}
                  </div>
                </div>
              )}

              {/* Amber banner for production orders */}
              {selectedIssue.mo_number && (
                <div style={{ padding: '8px 12px', background: '#fff7e0', border: '1px solid #f0c040', fontSize: 13, lineHeight: '1.4' }}>
                  Phiếu xuất theo lệnh SX {selectedIssue.mo_number}: giảm số lượng hoặc hủy phiếu = <b>trả hàng về kho</b> và trả lại số đang giữ cho lệnh SX (dùng khi phát rồi nhưng không dùng được, phải đổi vật tư). Muốn đổi sang vật tư khác: trả ở đây rồi xuất vật tư mới ở màn Thực hiện sản xuất.
                </div>
              )}

              {/* Lines Table */}
              <div style={{ border: '1px solid #bbb', maxHeight: '40vh', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ background: '#f0f0f0', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 40, textAlign: 'center' }}>STT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left' }}>Tên vật tư</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left', width: 130 }}>Nhóm</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'center', width: 60 }}>ĐVT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'right', width: 100 }}>SL xuất</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'right', width: 90 }}>SL đã ghi</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left', width: 220 }}>Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedIssue.lines || []).map((l, i) => {
                      const mat = materialMap.get(l.material_id)
                      const origQty = Number(l.qty_base || 0)
                      return (
                        <tr key={l.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '4px 8px', textAlign: 'center', background: '#f9f9f9', border: '1px solid #ddd' }}>{i + 1}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>{l.material_name || mat?.material_name || 'Vật tư'}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>{getGroupDef(l.group_code || mat?.group_code).name}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'center', border: '1px solid #ddd' }}>{l.base_uom || mat?.base_uom || ''}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd' }}>
                            {isEditing ? (
                              <input
                                type="number"
                                min={0.01}
                                step="any"
                                value={editLineQtys[l.id] ?? origQty}
                                onChange={(e) => {
                                  const val = Number(e.target.value)
                                  setEditLineQtys((prev) => ({ ...prev, [l.id]: val }))
                                }}
                                style={{ width: '100%', textAlign: 'right', border: '1px solid #8aa', padding: '2px 4px' }}
                              />
                            ) : (
                              fmtN(origQty)
                            )}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd', color: '#666' }}>{fmtN(origQty)}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>
                            {isEditing ? (
                              <input
                                type="text"
                                value={editLineNotes[l.id] ?? (l.note || '')}
                                onChange={(e) => {
                                  const val = e.target.value
                                  setEditLineNotes((prev) => ({ ...prev, [l.id]: val }))
                                }}
                                style={{ width: '100%', border: '1px solid #8aa', padding: '2px 4px' }}
                              />
                            ) : (
                              l.note || '—'
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Editing reason */}
              {isEditing && (
                <div>
                  <label style={{ fontSize: 12, color: '#b00020', fontWeight: 600, display: 'block', marginBottom: 2 }}>
                    Lý do chỉnh sửa *
                  </label>
                  <input
                    type="text"
                    placeholder="VD: Trả lại vật tư hỏng, bớt số lượng..."
                    value={editReason}
                    onChange={(e) => setEditReason(e.target.value)}
                    style={{ width: '100%', border: '1px solid #c00', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              {/* Change history */}
              <div>
                <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 4, fontWeight: 600 }}>
                  Lịch sử thay đổi
                </label>
                <div style={{ border: '1px solid #ddd', maxHeight: 140, overflowY: 'auto', fontSize: 12, padding: '4px 8px', background: '#fafafa' }}>
                  {issueHistory.length === 0 ? (
                    <div style={{ color: '#888', padding: 4 }}>Chưa có thay đổi nào được ghi lại</div>
                  ) : (
                    issueHistory.map((h, i) => (
                      <div key={i} style={{ padding: '4px 0', borderBottom: '1px dotted #ccc' }}>
                        <b>{h.action === 'PRINT' ? 'Đã in phiếu' : h.action === 'VOID' ? 'Đã hủy phiếu' : (h.action || 'Thay đổi')}</b> · {h.by} · {formatDateTime(h.at)}
                        {h.reason && <div style={{ color: '#666' }}>{h.reason}</div>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderTop: '1px solid #bbb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      setEditReason('')
                    }}
                    style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
                  >
                    ✕ Bỏ sửa
                  </button>
                  <button
                    disabled={Boolean(
                      (selectedIssue.receiver_name && !editReceiverName.trim()) ||
                      (selectedIssue.receiver_department && !editReceiverDepartment.trim())
                    )}
                    onClick={() => void handleSaveIssueEdit()}
                    style={{
                      padding: '5px 14px',
                      background: Boolean(
                        (selectedIssue.receiver_name && !editReceiverName.trim()) ||
                        (selectedIssue.receiver_department && !editReceiverDepartment.trim())
                      )
                        ? '#888'
                        : '#1f6feb',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 600,
                      cursor: Boolean(
                        (selectedIssue.receiver_name && !editReceiverName.trim()) ||
                        (selectedIssue.receiver_department && !editReceiverDepartment.trim())
                      )
                        ? 'not-allowed'
                        : 'pointer',
                      opacity: Boolean(
                        (selectedIssue.receiver_name && !editReceiverName.trim()) ||
                        (selectedIssue.receiver_department && !editReceiverDepartment.trim())
                      )
                        ? 0.6
                        : 1,
                    }}
                  >
                    ✓ Xác nhận sửa
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setSelectedIssue(null)}
                    style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
                  >
                    Đóng
                  </button>
                  <button
                    onClick={() => handleOpenPrintIssue(selectedIssue)}
                    style={{
                      padding: '5px 14px',
                      background: '#1f6feb',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Printer size={14} aria-hidden="true" /> In phiếu
                  </button>
                  {canWriteInventory && selectedIssue.status !== 'VOID' && (
                    <>
                      <button
                        onClick={() => setIsEditing(true)}
                        style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
                      >
                        {selectedIssue.mo_number ? '✎ Sửa / trả lại kho' : '✎ Sửa phiếu'}
                      </button>
                      <button
                        onClick={() => void handleVoidIssue()}
                        style={{ padding: '5px 14px', background: '#fff', border: '1px solid #d32f2f', color: '#d32f2f', cursor: 'pointer' }}
                      >
                        Hủy phiếu
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Tạo phiếu xuất mới */}
      {newModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 14,
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #777',
              width: 'min(900px, 98vw)',
              maxHeight: '94vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
            }}
          >
            {/* Header */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderBottom: '1px solid #bbb', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>Tạo phiếu xuất</span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => setNewModalOpen(false)}
                style={{ border: 'none', background: 'none', fontSize: 16, cursor: 'pointer', padding: 4 }}
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '10px 14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#b00020', fontWeight: 600, display: 'block', marginBottom: 2 }}>
                    Người nhận hàng *
                  </label>
                  <Typeahead<string>
                    items={receiverSuggestions}
                    value={newReceiverName}
                    onChange={(val) => setNewReceiverName(val || '')}
                    displayValue={(s) => s}
                    inputValue={newReceiverName}
                    onInputChange={(val) => setNewReceiverName(val)}
                    allowCustomValue={true}
                    placeholder="Nhập hoặc chọn người nhận..."
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#b00020', fontWeight: 600, display: 'block', marginBottom: 2 }}>
                    Bộ phận *
                  </label>
                  <Typeahead<string>
                    items={DEPT_SUGGESTIONS}
                    value={newReceiverDepartment}
                    onChange={(val) => setNewReceiverDepartment(val || '')}
                    displayValue={(s) => s}
                    inputValue={newReceiverDepartment}
                    onInputChange={(val) => setNewReceiverDepartment(val)}
                    allowCustomValue={true}
                    placeholder="Nhập hoặc chọn bộ phận..."
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Mục đích *</label>
                  <select
                    value={newPurpose}
                    onChange={(e) => setNewPurpose(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', background: '#fff' }}
                  >
                    <option value="Xuất sửa chữa / mẫu">Xuất sửa chữa / mẫu</option>
                    <option value="Xuất sản xuất (không theo giữ chỗ)">Xuất sản xuất (không theo giữ chỗ)</option>
                    <option value="Trả nhà cung cấp">Trả nhà cung cấp</option>
                    <option value="Khác">Khác</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#444', display: 'block', marginBottom: 2 }}>Ghi chú phiếu</label>
                  <input
                    type="text"
                    placeholder="VD: Xuất mẫu cho khách xem…"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    style={{ width: '100%', border: '1px solid #999', padding: '6px 8px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Lines Table */}
              <div
                onPaste={handlePasteNewLines}
                style={{ border: '1px solid #bbb', maxHeight: '42vh', overflow: 'auto' }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ background: '#f0f0f0', position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 40, textAlign: 'center' }}>STT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left', minWidth: 260 }}>Vật tư * (có thể dán từ Excel)</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'center', width: 70 }}>ĐVT</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'right', width: 100 }}>Có thể dùng</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'right', width: 110 }}>SL xuất *</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', textAlign: 'left', width: 200 }}>Ghi chú</th>
                      <th style={{ padding: '5px 8px', border: '1px solid #ccc', width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newLines.map((l, i) => {
                      const selMat = materialMap.get(l.materialId)
                      const maxAvail = selMat ? Math.max(0, selMat.available_to_reserve ?? ((selMat.physical_available ?? selMat.available ?? 0) - (selMat.reserved_base ?? 0))) : 0
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '4px 8px', textAlign: 'center', background: '#f9f9f9', border: '1px solid #ddd' }}>{i + 1}</td>
                          <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>
                            <MaterialTypeahead
                              materials={materials}
                              value={l.materialId}
                              onChange={(val) => {
                                setNewLines((prev) =>
                                  prev.map((item, idx) => (idx === i ? { ...item, materialId: val, error: undefined } : item)),
                                )
                              }}
                              placeholder="Gõ tên hoặc thông số để chọn…"
                              error={l.error}
                            />
                            {l.error && (
                              <div style={{ fontSize: 11, color: '#c00', marginTop: 2, fontWeight: 500 }}>
                                {l.error}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'center', border: '1px solid #ddd' }}>
                            {selMat?.base_uom || selMat?.uom_default || '—'}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd' }}>
                            {selMat ? fmtN(maxAvail) : '—'}
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', border: '1px solid #ddd' }}>
                            <input
                              type="number"
                              min={0.01}
                              max={maxAvail}
                              step="any"
                              value={l.qty}
                              onChange={(e) => {
                                const val = e.target.value
                                const n = Number(val)
                                const clamped = n > maxAvail ? maxAvail : val === '' ? '' : Math.max(0, n)
                                setNewLines((prev) =>
                                  prev.map((item, idx) => (idx === i ? { ...item, qty: clamped } : item)),
                                )
                              }}
                              style={{ width: '100%', textAlign: 'right', border: '1px solid #8aa', padding: '2px 4px' }}
                            />
                          </td>
                          <td style={{ padding: '4px 8px', border: '1px solid #ddd' }}>
                            <input
                              type="text"
                              value={l.note}
                              onChange={(e) => {
                                const val = e.target.value
                                setNewLines((prev) =>
                                  prev.map((item, idx) => (idx === i ? { ...item, note: val } : item)),
                                )
                              }}
                              style={{ width: '100%', border: '1px solid #8aa', padding: '2px 4px' }}
                            />
                          </td>
                          <td style={{ padding: '4px 8px', textAlign: 'center', border: '1px solid #ddd' }}>
                            <button
                              onClick={() => setNewLines((prev) => prev.filter((_, idx) => idx !== i))}
                              style={{ border: 'none', background: 'none', color: '#b00020', cursor: 'pointer', fontWeight: 700 }}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Add line button */}
              <div>
                <button
                  onClick={() => setNewLines((prev) => [...prev, { materialId: '', qty: '', note: '' }])}
                  style={{ padding: '4px 10px', background: '#f6f8fa', border: '1px solid #ccc', borderRadius: 2, cursor: 'pointer' }}
                >
                  ＋ Thêm dòng
                </button>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '9px 14px', background: '#f3f3f3', borderTop: '1px solid #bbb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setNewModalOpen(false)}
                style={{ padding: '5px 14px', background: '#fff', border: '1px solid #aaa', cursor: 'pointer' }}
              >
                Hủy
              </button>
              <button
                onClick={() => void handleSaveNewIssue()}
                style={{ padding: '5px 14px', background: '#1f6feb', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
              >
                Lưu phiếu xuất
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: In phiếu xuất kho */}
      <WarehouseDocumentPrintModal
        isOpen={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        data={printDocData}
        onPrint={() => {
          if (printIssueId) {
            void recordStockIssuePrintLog(printIssueId)
              .then(() => {
                if (selectedIssue?.id === printIssueId) {
                  getStockIssueHistory(printIssueId)
                    .then((h) => {
                      if (h) setIssueHistory(h)
                    })
                    .catch(() => {})
                }
              })
              .catch(() => {})
          }
        }}
      />
    </div>
  )
}
